import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Plus, MapPin, Bell, Calendar, TrendingUp, Info, Home, RefreshCw, Map, Users, MessageSquare, Radio } from 'lucide-react';
import { InFeedAd } from '@/components/AdBanner';
import { useLivePosts, LivePostsBanner } from '@/components/LivePosts';
import OnboardingModal from '@/components/OnboardingModal';
import PostCard, { PostCardSkeleton } from '@/components/PostCard';
import { Page, PageContent, EmptyState } from '@/components/layout';
import { Logo } from '@/components/Logo';
import { COMMUNES } from '@/seo/communes';

const CATEGORIES = [
  { id: '', name: 'Tout', icon: Home },
  { id: 'alerte', name: 'Alertes', icon: Bell, color: 'text-red-500' },
  { id: 'evenement', name: 'Événements', icon: Calendar, color: 'text-amber-500' },
  { id: 'bon_plan', name: 'Bons Plans', icon: TrendingUp, color: 'text-emerald-500' },
  { id: 'info', name: 'Infos', icon: Info, color: 'text-cyan-500' },
];

const WsIndicator = ({ wsConnected, wsReconnecting, reconnect }) => (
  <button
    onClick={!wsConnected && !wsReconnecting ? reconnect : undefined}
    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-mono font-bold uppercase tracking-wider transition-all ${
      wsConnected ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
        : wsReconnecting ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 animate-pulse cursor-wait'
        : 'bg-red-500/10 text-red-600 dark:text-red-400 hover:bg-red-500/20 cursor-pointer'
    }`}
    title={wsConnected ? 'Connecté en temps réel' : wsReconnecting ? 'Reconnexion...' : 'Cliquez pour reconnecter'}
    data-testid="ws-status-indicator"
  >
    <span className={`w-1.5 h-1.5 rounded-full ${wsConnected ? 'bg-emerald-500' : wsReconnecting ? 'bg-amber-500 animate-pulse' : 'bg-red-500'}`} />
    <span className="hidden sm:inline">{wsConnected ? 'Live' : wsReconnecting ? 'Reconnexion' : 'Hors ligne'}</span>
  </button>
);

const IconBtn = ({ onClick, icon: Icon, testId, spin, disabled, label }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    className="h-10 w-10 rounded-xl border border-border/70 bg-card inline-flex items-center justify-center text-foreground/80 hover:text-primary hover:border-primary/40 transition-colors disabled:opacity-50"
    data-testid={testId}
  >
    <Icon className={`h-[18px] w-[18px] ${spin ? 'animate-spin' : ''}`} />
  </button>
);

const FeedPage = () => {
  const navigate = useNavigate();
  const { user, api, refreshUser, wsConnected, wsReconnecting, reconnectWebSocket } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedCommune, setSelectedCommune] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [playingAudio, setPlayingAudio] = useState(null);
  const [followingOnly, setFollowingOnly] = useState(false);
  const [following, setFollowing] = useState([]);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    if (user && !user.onboarding_completed) setShowOnboarding(true);
  }, [user]);

  const communes = [{ id: '', name: 'Toutes' }, ...COMMUNES.map((c) => ({ id: c.id, name: c.name }))];

  const liveMatches = useCallback((post) => (
    (!selectedCommune || post.commune === selectedCommune) &&
    (!selectedCategory || post.category === selectedCategory) &&
    (!followingOnly || following.includes(post.user_id))
  ), [selectedCommune, selectedCategory, followingOnly, following]);
  const live = useLivePosts({ userId: user?.user_id, matches: liveMatches });

  const showLivePosts = () => {
    const fresh = live.take().map((p) => ({ ...p, is_liked: false, is_following_author: following.includes(p.user_id), reactions: {}, user_reaction: null }));
    setPosts((prev) => [...fresh, ...prev.filter((p) => !fresh.some((f) => f.post_id === p.post_id))]);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const fetchPosts = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedCommune) params.append('commune', selectedCommune);
      if (selectedCategory) params.append('category', selectedCategory);
      const response = await api.get(`/posts?${params.toString()}`);
      let filteredPosts = response.data;
      if (followingOnly && following.length > 0) {
        filteredPosts = filteredPosts.filter((post) => following.includes(post.user_id));
      }
      setPosts(filteredPosts);
    } catch (error) {
      toast.error('Erreur lors du chargement des posts');
    } finally {
      setLoading(false);
    }
  }, [api, selectedCommune, selectedCategory, followingOnly, following]);

  useEffect(() => {
    const fetchFollowing = async () => {
      if (!user?.user_id) return;
      try {
        const response = await api.get(`/users/${user.user_id}/following`);
        setFollowing(response.data.map((u) => u.user_id));
      } catch (error) {
        console.error('Error fetching following:', error);
      }
    };
    fetchFollowing();
  }, [user, api]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchPosts();
    setRefreshing(false);
    toast.success('Fil actualisé !');
  };

  const handleLike = async (postId) => {
    try {
      const response = await api.post(`/posts/${postId}/like`);
      updatePost(postId, { is_liked: response.data.liked, likes_count: response.data.likes_count });
    } catch (error) {
      toast.error('Erreur lors du like');
    }
  };

  const updatePost = (postId, fields) => setPosts((prev) => prev.map((p) => (p.post_id === postId ? { ...p, ...fields } : p)));

  return (
    <Page testId="feed-page">
      <header className="sticky top-0 md:top-16 z-40 glass border-b">
        <div className="container mx-auto max-w-2xl px-4 sm:px-6 pt-3 pb-2">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="md:hidden"><Logo /></span>
              <span className="hidden md:inline-flex items-center gap-2 text-lg font-bold"><Radio className="h-5 w-5 text-primary" /> Fil d'actualités</span>
              {user && <WsIndicator wsConnected={wsConnected} wsReconnecting={wsReconnecting} reconnect={reconnectWebSocket} />}
            </div>
            <div className="flex items-center gap-2">
              <IconBtn onClick={() => navigate('/chat')} icon={MessageSquare} testId="chat-btn" label="Chat de quartier" />
              <IconBtn onClick={() => navigate('/map')} icon={Map} testId="map-btn" label="Carte" />
              <IconBtn onClick={handleRefresh} icon={RefreshCw} testId="refresh-btn" spin={refreshing} disabled={refreshing} label="Actualiser" />
            </div>
          </div>

          {user && following.length > 0 && (
            <label className="flex items-center justify-between mt-3 px-3.5 py-2.5 rounded-xl bg-card border border-border/60 cursor-pointer">
              <span className="flex items-center gap-2 text-sm font-medium"><Users className="h-4 w-4 text-primary" /> Abonnements uniquement</span>
              <Switch checked={followingOnly} onCheckedChange={setFollowingOnly} data-testid="following-filter-switch" />
            </label>
          )}

          <div className="flex gap-2 overflow-x-auto no-scrollbar mt-3 -mx-4 px-4 sm:mx-0 sm:px-0 pb-1">
            {communes.map((commune) => (
              <button
                key={commune.id}
                className={`chip ${selectedCommune === commune.id ? 'chip-active' : ''}`}
                onClick={() => setSelectedCommune(commune.id)}
                data-testid={`commune-filter-${commune.id || 'all'}`}
              >
                <MapPin className="h-3.5 w-3.5" />
                {commune.name}
              </button>
            ))}
          </div>

          <div className="flex gap-2 overflow-x-auto no-scrollbar mt-2 -mx-4 px-4 sm:mx-0 sm:px-0 pb-1">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                className={`chip ${selectedCategory === cat.id ? 'chip-active' : ''}`}
                onClick={() => setSelectedCategory(cat.id)}
                data-testid={`category-filter-${cat.id || 'all'}`}
              >
                <cat.icon className={`h-3.5 w-3.5 ${selectedCategory === cat.id ? '' : cat.color || ''}`} />
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </header>
      <LivePostsBanner count={live.pending.length} onShow={showLivePosts} />

      <PageContent>
        {loading ? (
          <div className="space-y-4">{[1, 2, 3].map((i) => <PostCardSkeleton key={i} />)}</div>
        ) : posts.length === 0 ? (
          <EmptyState
            emoji="🌴"
            title="Aucun post pour le moment"
            description="Soyez le premier à partager quelque chose dans votre commune !"
            action={
              <button onClick={() => navigate('/create')} className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-md btn-bounce" data-testid="empty-create-btn">
                <Plus className="h-4 w-4" /> Créer un post
              </button>
            }
          />
        ) : (
          <div className="space-y-4 stagger-children">
            {posts.map((post, index) => (
              <div key={post.post_id} className="space-y-4">
                {index > 0 && index % 4 === 0 && <InFeedAd />}
                <PostCard
                  post={post}
                  api={api}
                  onUpdate={updatePost}
                  onLike={handleLike}
                  onVote={fetchPosts}
                  playingAudio={playingAudio}
                  setPlayingAudio={setPlayingAudio}
                />
              </div>
            ))}
          </div>
        )}
      </PageContent>

      <button
        className="md:hidden fixed bottom-24 right-4 h-14 w-14 rounded-2xl volcano-gradient text-white shadow-xl shadow-primary/30 inline-flex items-center justify-center btn-bounce animate-pulse-glow"
        onClick={() => navigate('/create')}
        aria-label="Créer un post"
        data-testid="fab-create-btn"
      >
        <Plus className="h-6 w-6" />
      </button>

      {showOnboarding && (
        <OnboardingModal onComplete={() => { setShowOnboarding(false); refreshUser?.(); }} />
      )}
    </Page>
  );
};

export default FeedPage;
